import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { clampToYears } from "@/shared/years";

/** `key=1` in the URL means true; anything else (including absent) is false. */
export function parseQueryFlag(raw: string | null): boolean {
  return raw === "1";
}

/**
 * Resolve a year search param against the offered list. Invalid or stale values clamp
 * to the nearest offered year; missing values use the clamped fallback.
 */
export function parseQueryYear(raw: string | null, years: number[], fallbackYear: number): number {
  if (raw === null || raw === "") {
    return clampToYears(fallbackYear, years);
  }
  const parsed = Number.parseInt(raw, 10);
  if (!Number.isFinite(parsed) || String(parsed) !== raw) {
    return clampToYears(fallbackYear, years);
  }
  if (years.length > 0 && !years.includes(parsed)) {
    return clampToYears(parsed, years);
  }
  return parsed;
}

/** Default year selections are omitted from the URL to keep share links clean. */
export function shouldOmitYear(year: number, fallbackYear: number, years: number[]): boolean {
  const effectiveFallback = clampToYears(fallbackYear, years);
  return year === fallbackYear || year === effectiveFallback;
}

/**
 * Apply several search-param updates onto a copy of `params`.
 * `null` or a value equal to that key's fallback (default `""`) deletes the key.
 */
export function patchSearchParams(
  params: URLSearchParams,
  updates: Record<string, string | null>,
  fallbacks: Record<string, string> = {},
): URLSearchParams {
  const nextParams = new URLSearchParams(params);
  for (const [key, next] of Object.entries(updates)) {
    const fallback = fallbacks[key] ?? "";
    if (next === null || next === fallback) {
      nextParams.delete(key);
    } else {
      nextParams.set(key, next);
    }
  }
  return nextParams;
}

/**
 * Sync a string (or id) filter to a URL search param. Optional `allowed` rejects unknown
 * values to `fallback`. Setting the fallback removes the param. Updates use `replace: true`.
 */
export function useQueryParam(
  key: string,
  options: { allowed?: readonly string[]; fallback: string },
): [string, (next: string) => void] {
  const { allowed, fallback } = options;
  const [params, setParams] = useSearchParams();
  const raw = params.get(key);
  const value =
    allowed !== undefined
      ? allowed.includes(raw as string)
        ? (raw as string)
        : fallback
      : (raw ?? fallback);

  const setValue = useCallback(
    (next: string) => {
      setParams(patchSearchParams(params, { [key]: next }, { [key]: fallback }), {
        replace: true,
      });
    },
    [fallback, key, params, setParams],
  );

  return [value, setValue];
}

const EMPTY_FALLBACKS: Record<string, string> = {};

/**
 * Apply multiple search-param updates in a single `setSearchParams` call (`replace: true`).
 * Avoids races when two `useQueryParam` setters each clone the same stale params.
 * `null` or a value equal to that key's fallback (default `""`) deletes the key.
 */
export function usePatchQueryParams(
  fallbacks: Record<string, string> = EMPTY_FALLBACKS,
): (updates: Record<string, string | null>) => void {
  const [, setParams] = useSearchParams();

  return useCallback(
    (updates: Record<string, string | null>) => {
      setParams((params) => patchSearchParams(params, updates, fallbacks), { replace: true });
    },
    [fallbacks, setParams],
  );
}

/** Boolean filter encoded as `key=1`; false omits the param. Updates use `replace: true`. */
export function useQueryFlag(key: string): [boolean, (next: boolean) => void] {
  const [params, setParams] = useSearchParams();
  const value = parseQueryFlag(params.get(key));

  const setValue = useCallback(
    (next: boolean) => {
      const nextParams = new URLSearchParams(params);
      if (next) {
        nextParams.set(key, "1");
      } else {
        nextParams.delete(key);
      }
      setParams(nextParams, { replace: true });
    },
    [key, params, setParams],
  );

  return [value, setValue];
}

/**
 * Sync a year filter to a URL search param. Values outside the offered list clamp on read;
 * the effective default (clamped fallback) is omitted on write. Updates use `replace: true`.
 */
export function useQueryYear(
  key: string,
  years: number[],
  fallbackYear: number,
): [number, (next: number) => void] {
  const [params, setParams] = useSearchParams();
  const value = parseQueryYear(params.get(key), years, fallbackYear);

  const setValue = useCallback(
    (next: number) => {
      const nextParams = new URLSearchParams(params);
      if (shouldOmitYear(next, fallbackYear, years)) {
        nextParams.delete(key);
      } else {
        nextParams.set(key, String(next));
      }
      setParams(nextParams, { replace: true });
    },
    [fallbackYear, key, params, setParams, years],
  );

  return [value, setValue];
}
